import { useState } from "react";
import { ChevronDown, ChevronRight, Download, ListPlus, RotateCcw, Save, Trash2 } from "lucide-react";
import { Button, Card, CardBody, CardHeader, EmptyState, Field, Input, Textarea } from "@/components/ui";
import { QuestionEditor } from "./QuestionEditor";
import type { Dataset, DatasetQuestion } from "./types";

/** Collapsible JSON editor. Like before, only valid JSON is applied (invalid edits are ignored). */
function JsonPanel({ label, description, value, onChange }: { label: string; description: string; value: any; onChange: (parsed: any) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-border pt-4">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between gap-3 text-left" aria-expanded={open}>
        <span>
          <span className="block text-sm font-medium">{label}</span>
          <span className="block text-xs text-muted-foreground">{description}</span>
        </span>
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>
      {open && (
        <Textarea
          value={JSON.stringify(value || {}, null, 2)}
          onChange={(e) => {
            try {
              onChange(JSON.parse(e.target.value));
            } catch {
              // Invalid JSON, don't update
            }
          }}
          className="mt-3 font-mono text-[13px]"
          rows={8}
          spellCheck={false}
          placeholder='{"key": "value"}'
        />
      )}
    </div>
  );
}

/** Builds the cleaned JSON export (only current fields) and downloads it. */
function exportDataset(dataset: Dataset) {
  const cleanedDataset = {
    ...dataset,
    questions: dataset.questions.map((q) => ({
      id: q.id,
      ...(q.domain && { domain: q.domain }),
      question: q.question,
      ...(q.question_en && { question_en: q.question_en }),
      difficulty: q.difficulty,
      ...(q.expected_tables && q.expected_tables.length > 0 && { expected_tables: q.expected_tables }),
      reference_sql: q.reference_sql,
      ...(q.human_readable_answer && { human_readable_answer: q.human_readable_answer }),
      ...(q.explanation && { explanation: q.explanation }),
    })),
  };

  const json = JSON.stringify(cleanedDataset, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `dataset_${dataset.version}.json`;
  a.click();
}

export function DatasetEditor({
  dataset,
  onChange,
  loading,
  canReset,
  editingQuestionIndex,
  onToggleQuestion,
  onUpdateQuestion,
  onAddQuestion,
  onRemoveQuestion,
  onDeleteAllQuestions,
  onSave,
  onReset,
  onCancel,
}: {
  dataset: Dataset;
  onChange: (dataset: Dataset) => void;
  loading: boolean;
  canReset: boolean;
  editingQuestionIndex: number | null;
  onToggleQuestion: (index: number) => void;
  onUpdateQuestion: (index: number, field: keyof DatasetQuestion, value: any) => void;
  onAddQuestion: () => void;
  onRemoveQuestion: (index: number) => void;
  onDeleteAllQuestions: () => void;
  onSave: () => void;
  onReset: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="space-y-6">
      {/* Action bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">
          Vragenlijst bewerken: <span className="font-mono">{dataset.version}</span>
        </h2>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => exportDataset(dataset)}>
            <Download className="h-4 w-4" />
            JSON exporteren
          </Button>
          <Button variant="outline" onClick={onReset} disabled={!canReset} title={canReset ? undefined : "Nog niet opgeslagen: niets om te herstellen"}>
            <RotateCcw className="h-4 w-4" />
            Herstellen
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Annuleren
          </Button>
          <Button variant="brand" onClick={onSave} loading={loading}>
            {!loading && <Save className="h-4 w-4" />}
            {loading ? "Opslaan…" : "Wijzigingen opslaan"}
          </Button>
        </div>
      </div>

      {/* Metadata */}
      <Card>
        <CardHeader title="Gegevens van de vragenlijst" />
        <CardBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Versie" htmlFor="ds-version" hint="Een nieuwe versie maakt een nieuwe vragenlijst aan.">
              <Input id="ds-version" value={dataset.version} onChange={(e) => onChange({ ...dataset, version: e.target.value })} className="font-mono" />
            </Field>
            <Field label="Naam" htmlFor="ds-name">
              <Input id="ds-name" value={dataset.name} onChange={(e) => onChange({ ...dataset, name: e.target.value })} />
            </Field>
            <Field label="Omschrijving" htmlFor="ds-description" className="sm:col-span-2">
              <Textarea id="ds-description" value={dataset.description} onChange={(e) => onChange({ ...dataset, description: e.target.value })} rows={3} />
            </Field>
            <Field label="Domeinen" htmlFor="ds-domains" hint="Gescheiden door komma's.">
              <Input
                id="ds-domains"
                value={(dataset.domains || []).join(", ")}
                onChange={(e) =>
                  onChange({
                    ...dataset,
                    domains: e.target.value
                      .split(",")
                      .map((d) => d.trim())
                      .filter((d) => d),
                  })
                }
              />
            </Field>
            <Field label="Aanmaakdatum" htmlFor="ds-created">
              <Input id="ds-created" type="date" value={dataset.created_date || ""} onChange={(e) => onChange({ ...dataset, created_date: e.target.value })} />
            </Field>
          </div>

          <JsonPanel
            label="Richtlijnen"
            description="Richtlijnen voor deze vragenlijst (JSON)."
            value={dataset.guidelines}
            onChange={(parsed) => onChange({ ...dataset, guidelines: parsed })}
          />
          <JsonPanel
            label="Layer 3-architectuur"
            description="Beschrijving van de Layer 3-architectuur (JSON)."
            value={dataset.layer3_architecture}
            onChange={(parsed) => onChange({ ...dataset, layer3_architecture: parsed })}
          />
        </CardBody>
      </Card>

      {/* Questions */}
      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-semibold">
            Vragen <span className="font-normal text-muted-foreground">({dataset.questions.length})</span>
          </h3>
          <div className="flex gap-2">
            {dataset.questions.length > 0 && (
              <Button variant="ghost" onClick={onDeleteAllQuestions} className="text-destructive hover:bg-[#FDECEC]">
                <Trash2 className="h-4 w-4" />
                Alles verwijderen
              </Button>
            )}
            <Button onClick={onAddQuestion}>
              <ListPlus className="h-4 w-4" />
              Vraag toevoegen
            </Button>
          </div>
        </div>

        {dataset.questions.length === 0 ? (
          <Card>
            <EmptyState
              title="Nog geen vragen"
              description="Voeg een vraag toe met de referentie-SQL waartegen de agent beoordeeld wordt."
              action={
                <Button variant="brand" onClick={onAddQuestion}>
                  <ListPlus className="h-4 w-4" />
                  Vraag toevoegen
                </Button>
              }
            />
          </Card>
        ) : (
          <div className="space-y-3">
            {dataset.questions.map((q, index) => (
              <QuestionEditor
                key={q.id}
                question={q}
                index={index}
                expanded={editingQuestionIndex === index}
                onToggle={() => onToggleQuestion(index)}
                onChange={(field, value) => onUpdateQuestion(index, field, value)}
                onRemove={() => onRemoveQuestion(index)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
